import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindInstance } from "redux-mvc";
import { TextAreaInput } from "ui-kit/Form/View/TextAreaInput";

import { noop } from "utils/General";

import { actions } from "ui-kit/Form/model";
import { getValue } from "ui-kit/Form/selectors";
import { FIELD_TYPES, TINY_MCE_EXTERNAL_PLUGINS } from "ui-kit/Form/constants";

import { Editor } from "@tinymce/tinymce-react";

import { WithFormInstanceConsumer } from "ui-kit/Form/View/Context";
import { maxLengthPlugin } from "./AdvancedWYSIWYGWithoutSagas";
import { captureException } from "utils/errors";

export const DEFAULT_MODULES = "table media link code lists image codesample";
export const DEFAULT_FORMATS =
  "bold italic underline | superscript subscript link | bullist numlist outdent indent | image | removeformat | code | codesample";

// for more formaters check https://www.tiny.cloud/docs/integrations/react/

const decorate = R.compose(
  WithFormInstanceConsumer({ fieldType: FIELD_TYPES.VALUE }),
  connect(
    (state, props) => ({
      value: getValue(state, props)
    }),
    bindInstance({
      onChange: actions.setFieldValueRequest,
      onFocus: actions.focusField,
      onBlur: actions.blurField,
      setIniValue: actions.setIniValue,
      uploadWYSIWYGImg: actions.uploadWYSIWYGImg
    })
  )
);

export const AdvancedWYSIWYG = decorate(
  ({
    value = "",
    iniValue,
    onChange = noop,
    onFocus = noop,
    onBlur = noop,
    setIniValue = noop,
    uploadWYSIWYGImg = noop,
    fieldId,
    fieldType,
    modules = DEFAULT_MODULES,
    formats = DEFAULT_FORMATS,
    placeholder = "Content...",
    height,
    width = "100%",
    settings = {},
    maxLength = null,
    tag = undefined,
    // This setting allows this editor to still be used but without
    // any of the formatting settings or toolbar. Good for crafting SMS message.
    useWYSIWYG = true,
    ariaLabel = "Rich text editor",
    disabled,
    useCustomCharCount = false, // tinyMCE doesnt have charcount plugin, just wordcount, so we need to use our own counter
    allowIframes = false
  }) => {
    const [useFallbackEditor, setUseFallbackEditor] = useState(false);

    const editorRef = useRef(null);
    const [charCounts, setCharCounts] = useState(iniValue?.length || 0);
    const isCharCountsEnabled = !R.isNil(maxLength) && useCustomCharCount;

    useEffect(() => {
      setIniValue(iniValue, {
        meta: { fieldType, fieldId }
      });
    }, []);
    useEffect(() => {
      if (editorRef.current) {
        editorRef.current.insertContent(tag);
      }
    }, [tag]);

    if (useFallbackEditor) {
      return (
        <TextAreaInput
          {...{
            value,
            iniValue,
            setIniValue,
            onChange,
            onFocus,
            onBlur,
            fieldType,
            fieldId,
            maxLength,
            style: {
              width: "100%"
            },
            minRows: 10
          }}
        />
      );
    }

    return (
      <>
        <Editor
          tabIndex={0}
          disabled={disabled}
          aria-label={ariaLabel}
          onInit={(evt, editor) => (editorRef.current = editor)}
          // eslint-disable-next-line no-underscore-dangle
          apiKey={window.__TINY_MCE_API_KEY__}
          onScriptsLoadError={err => {
            captureException(err, {
              extra: {
                message: "[TinyMCE onScriptsLoadError] - Advanced With Sagas"
              }
            });
            setUseFallbackEditor(true);
          }}
          onFocus={() => onFocus(null, { meta: { fieldId, fieldType } })}
          // eslint-disable-next-line consistent-return
          onEditorChange={val => {
            onChange(val, { meta: { fieldId, fieldType } });
          }}
          onBlur={() => onBlur(null, { meta: { fieldId, fieldType } })}
          value={value}
          init={{
            height,
            width,
            placeholder,
            plugins: useWYSIWYG ? modules : "wordcount",
            toolbar: useWYSIWYG ? formats : "",
            statusbar: useWYSIWYG,
            relative_urls: false,
            remove_script_host: false,
            file_picker_callback: uploadWYSIWYGImg,
            setup: editor => {
              if (maxLength) {
                maxLengthPlugin(
                  editor,
                  maxLength,
                  useCustomCharCount,
                  useCustomCharCount ? setCharCounts : null
                );
              }

              // Allow iframes to be displayed by populating the sandbox attribute
              // @NOTE: In the future, we should look at using an allowlist of domains
              // instead of allowing all iframes to be displayed
              if (allowIframes) {
                editor.on("PreInit", function() {
                  editor.parser.addNodeFilter("iframe", function(nodes) {
                    nodes.forEach(function(node) {
                      node.attr("sandbox", "allow-scripts allow-same-origin");
                    });
                  });
                });
              }
            },
            default_link_target: "_blank",
            paste_as_text: true,
            menubar: false,
            branding: false,
            image_caption: true,
            image_title: true,
            urlconverter_callback: function(url) {
              return url;
            },
            external_plugins: {
              eqneditor: TINY_MCE_EXTERNAL_PLUGINS.eqneditor
            },
            ...settings
          }}
        />
        {isCharCountsEnabled ? (
          <div
            style={{
              fontSize: 12,
              padding: "4px 0 0 4px",
              color: "#828282",
              fontWeight: 600
            }}
          >
            {charCounts}/{maxLength} Character
            {charCounts !== 1 ? "s" : ""}
          </div>
        ) : null}
      </>
    );
  }
);
